import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';

/* ─── SVG Flag Components ─── */

const FlagSvg: React.FC<{ code: string; size?: number }> = ({ code, size = 20 }) => {
  const flags: Record<string, React.ReactNode> = {
    gb: (
      <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <clipPath id="gb-clip"><circle cx="18" cy="18" r="18"/></clipPath>
        <g clipPath="url(#gb-clip)">
          <rect width="36" height="36" fill="#012169"/>
          <path d="M0 0L36 36M36 0L0 36" stroke="#fff" strokeWidth="6"/>
          <path d="M0 0L36 36M36 0L0 36" stroke="#C8102E" strokeWidth="2"/>
          <path d="M18 0V36M0 18H36" stroke="#fff" strokeWidth="10"/>
          <path d="M18 0V36M0 18H36" stroke="#C8102E" strokeWidth="6"/>
        </g>
      </svg>
    ),
    ro: (
      <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <clipPath id="ro-clip"><circle cx="18" cy="18" r="18"/></clipPath>
        <g clipPath="url(#ro-clip)">
          <rect width="12" height="36" fill="#002B7F"/>
          <rect x="12" width="12" height="36" fill="#FCD116"/>
          <rect x="24" width="12" height="36" fill="#CE1126"/>
        </g>
      </svg>
    ),
    de: (
      <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <clipPath id="de-clip"><circle cx="18" cy="18" r="18"/></clipPath>
        <g clipPath="url(#de-clip)">
          <rect width="36" height="12" fill="#000"/>
          <rect y="12" width="36" height="12" fill="#DD0000"/>
          <rect y="24" width="36" height="12" fill="#FFCC00"/>
        </g>
      </svg>
    ),
    fr: (
      <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <clipPath id="fr-clip"><circle cx="18" cy="18" r="18"/></clipPath>
        <g clipPath="url(#fr-clip)">
          <rect width="12" height="36" fill="#002395"/>
          <rect x="12" width="12" height="36" fill="#fff"/>
          <rect x="24" width="12" height="36" fill="#ED2939"/>
        </g>
      </svg>
    ),
    it: (
      <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <clipPath id="it-clip"><circle cx="18" cy="18" r="18"/></clipPath>
        <g clipPath="url(#it-clip)">
          <rect width="12" height="36" fill="#009246"/>
          <rect x="12" width="12" height="36" fill="#fff"/>
          <rect x="24" width="12" height="36" fill="#CE2B37"/>
        </g>
      </svg>
    ),
    es: (
      <svg width={size} height={size} viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
        <clipPath id="es-clip"><circle cx="18" cy="18" r="18"/></clipPath>
        <g clipPath="url(#es-clip)">
          <rect width="36" height="9" fill="#AA151B"/>
          <rect y="9" width="36" height="18" fill="#F1BF00"/>
          <rect y="27" width="36" height="9" fill="#AA151B"/>
        </g>
      </svg>
    ),
  };

  return <>{flags[code] || null}</>;
};

/* ─── Styled Components ─── */

const LanguageDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  transition: all 0.2s ease;
  min-width: 65px;
  
  &:hover {
    border-color: #4263eb;
    background: #f8f9fa;
  }
  
  &:focus {
    outline: none;
    border-color: #4263eb;
    box-shadow: 0 0 0 3px rgba(66, 99, 235, 0.1);
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    min-height: 40px;
  }
`;

const FlagIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
  
  svg {
    display: block;
    border-radius: 50%;
  }
`;

const LanguageCode = styled.span`
  text-transform: uppercase;
  font-size: 13px;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const ChevronIcon = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  margin-left: 2px;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 140px;
  overflow: hidden;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 1000;
`;

const LanguageOption = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  background: ${props => props.$isActive ? '#f0f3ff' : 'white'};
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.$isActive ? 600 : 500};
  color: ${props => props.$isActive ? COLORS.PRIMARY : '#495057'};
  text-align: left;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    background: ${props => props.$isActive ? '#f0f3ff' : '#f8f9fa'};
    color: ${props => props.$isActive ? '#4263eb' : '#212529'};
  }
  
  &:focus {
    outline: none;
    background: #f0f3ff;
  }
  
  ${props => props.$isActive && `
    &::after {
      content: '✓';
      position: absolute;
      right: 14px;
      color: #4263eb;
      font-weight: 700;
    }
  `}
`;

const LanguageName = styled.span`
  flex: 1;
`;

const CompactLanguageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${COLORS.PRIMARY};
    background: #f8f9fa;
  }
  
  &:focus {
    outline: none;
    border-color: #4263eb;
    box-shadow: 0 0 0 3px rgba(66, 99, 235, 0.1);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

/* ─── Language Data ─── */

interface Language {
  code: string;
  name: string;
  flagCode: string;
  nativeName?: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', flagCode: 'gb', nativeName: 'English' },
  { code: 'ro', name: 'Romanian', flagCode: 'ro', nativeName: 'Română' },
  { code: 'de', name: 'German', flagCode: 'de', nativeName: 'Deutsch' },
  { code: 'fr', name: 'French', flagCode: 'fr', nativeName: 'Français' },
  { code: 'it', name: 'Italian', flagCode: 'it', nativeName: 'Italiano' },
  { code: 'es', name: 'Spanish', flagCode: 'es', nativeName: 'Español' },
];

/* ─── Component ─── */

interface LanguageSwitcherProps {
  variant?: 'default' | 'compact';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'default' 
}) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
    localStorage.setItem('preferredLanguage', languageCode);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (variant === 'compact') {
    return (
      <LanguageDropdownContainer ref={dropdownRef}>
        <CompactLanguageButton
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Change language"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <FlagIcon>
            <FlagSvg code={currentLanguage.flagCode} size={22} />
          </FlagIcon>
        </CompactLanguageButton>
        
        <DropdownMenu $isOpen={isOpen}>
          {languages.map(language => (
            <LanguageOption
              key={language.code}
              $isActive={language.code === currentLanguage.code}
              onClick={() => handleLanguageChange(language.code)}
              aria-label={`Switch to ${language.name}`}
            >
              <FlagIcon>
                <FlagSvg code={language.flagCode} size={20} />
              </FlagIcon>
              <LanguageName>{language.nativeName || language.name}</LanguageName>
            </LanguageOption>
          ))}
        </DropdownMenu>
      </LanguageDropdownContainer>
    );
  }

  return (
    <LanguageDropdownContainer ref={dropdownRef}>
      <LanguageButton
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <FlagIcon>
          <FlagSvg code={currentLanguage.flagCode} size={20} />
        </FlagIcon>
        <LanguageCode>{currentLanguage.code}</LanguageCode>
        <ChevronIcon $isOpen={isOpen}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </ChevronIcon>
      </LanguageButton>
      
      <DropdownMenu $isOpen={isOpen}>
        {languages.map(language => (
          <LanguageOption
            key={language.code}
            $isActive={language.code === currentLanguage.code}
            onClick={() => handleLanguageChange(language.code)}
            aria-label={`Switch to ${language.name}`}
          >
            <FlagIcon>
              <FlagSvg code={language.flagCode} size={20} />
            </FlagIcon>
            <LanguageName>{language.nativeName || language.name}</LanguageName>
          </LanguageOption>
        ))}
      </DropdownMenu>
    </LanguageDropdownContainer>
  );
};

export const useCurrentLanguage = () => {
  const { i18n } = useTranslation();
  return languages.find(lang => lang.code === i18n.language) || languages[0];
};